import {
	EXTERNAL_ID,
	GROUP,
	IdPCommonInputFieldName,
	IdPOidcInputFieldName,
	IdPSamlInputFieldName,
	IdPXaaInputFieldName,
	Method,
	XaaJit
} from 'consts';
import { isEmpty } from 'lodash';
import {
	IResAttribute,
	IConfigMapping,
	IGroupsAttributeMapping,
	IMethodConfigFields,
	IOAuthConfig,
	IResConfigs,
	ISamlConfig,
	ITenantConfig,
	IUserAttributeMapping,
	IXaaConfig,
	IXaaIssuerSettings
} from 'types/api';
import { ExcludeKeys } from 'types/typeutils';
import { getCustomSettingId, isDefaultSSOSettings } from './configUtils';
import { filterObjectByValues } from './objectUtils';

const castTypeExcludeNullValues = (obj: any) =>
	obj as ExcludeKeys<typeof obj, null>;

const updateSSODomains = (
	ssoDomains: string[],
	configsData: IResConfigs,
	method: Method
): IResConfigs => {
	if (isDefaultSSOSettings(configsData)) {
		return {
			...configsData!,
			tenant: {
				...configsData.tenant,
				domains: ssoDomains
			}
		};
	}

	const customSettingId = getCustomSettingId(configsData, method);

	return {
		...configsData!,
		tenant: {
			...configsData.tenant,
			domainsToSSO: Object.fromEntries([
				...Object.entries(configsData.tenant.domainsToSSO || {}).filter(
					([, settingId]) => settingId !== customSettingId
				),
				...ssoDomains.map((domain) => [domain, customSettingId])
			])
		}
	};
};

export const updateJITDisabled = (
	jitDisabled: boolean | undefined | null,
	configsData: IResConfigs
) => {
	if (isDefaultSSOSettings(configsData)) {
		const res = {
			...configsData,
			tenant: { ...configsData.tenant }
		} as IResConfigs;
		if (jitDisabled) {
			res.tenant.JITDisabled = jitDisabled;
		} else {
			delete res.tenant.JITDisabled;
		}
		return res;
	}

	const customSamlSettingId = getCustomSettingId(configsData, Method.Saml)!;
	const customOidcSettingId = getCustomSettingId(configsData, Method.Oidc)!;

	if (
		!!configsData.tenant.JITDisabledSSO?.[customSamlSettingId] !==
			!!jitDisabled ||
		!!configsData.tenant.JITDisabledSSO?.[customOidcSettingId] !== !!jitDisabled
	) {
		return {
			...configsData,
			tenant: {
				...configsData.tenant,
				JITDisabledSSO: {
					...configsData.tenant.JITDisabledSSO,
					[customSamlSettingId]: !!jitDisabled,
					[customOidcSettingId]: !!jitDisabled
				}
			}
		} as IResConfigs;
	}

	/* istanbul ignore next: no need to test this */
	return configsData;
};

// Cross-App Access JIT lives in the shared JIT section as its own toggle. It maps to the
// per-issuer jitDisabled and is applied to every trusted issuer so the whole XAA config
// shares one JIT policy (mirroring the single SSO JIT toggle).
export const updateXaaJITDisabled = (
	jitDisabled: boolean,
	configsData: IResConfigs
): IResConfigs => {
	const existingIssuers = configsData.xaa?.jwtBearerSettings?.issuers ?? {};
	const issuers = Object.fromEntries(
		Object.entries(existingIssuers).map(([key, issuer]) => [
			key,
			{ ...issuer, jitDisabled }
		])
	);

	return {
		...configsData,
		xaa: {
			...configsData.xaa,
			jwtBearerSettings: {
				...configsData.xaa?.jwtBearerSettings,
				issuers
			}
		}
	};
};

export const enableConfiguration = (
	method: Method,
	configsData: IResConfigs
): IResConfigs => {
	// XAA is enabled on its own config object and must not touch saml.enabled /
	// oauth.enabled or cast the method onto the tenant authType union.
	if (method === Method.Xaa) {
		return {
			...configsData,
			xaa: {
				...configsData.xaa,
				enabled: true
			}
		};
	}

	const res: IResConfigs = {
		...configsData,
		oauth: {
			...configsData.oauth,
			enabled: method === Method.Oidc
		},
		saml: {
			...configsData.saml,
			enabled: method === Method.Saml
		}
	};

	// Past the XAA early return, method is only SAML/OIDC, so it narrows safely
	// onto the tenant authType union.
	const authType = method as ITenantConfig['authType'];

	if (!configsData.currentSSOId) {
		res.tenant = {
			...res.tenant,
			authType
		};
		return res;
	}

	return {
		...res,
		tenant: {
			...res.tenant,
			SSOWrapperMap: {
				...res.tenant.SSOWrapperMap,
				[configsData.currentSSOId]: {
					...res.tenant.SSOWrapperMap![configsData.currentSSOId],
					authType
				}
			}
		}
	};
};

export const updateCommonFields = (
	methodConfigFields: IMethodConfigFields,
	configsData: IResConfigs,
	method: Method
): IResConfigs => {
	/* istanbul ignore next: cannot test this for now */
	if (!methodConfigFields?.[IdPCommonInputFieldName.SSODomains]) {
		return configsData;
	}

	const updated = updateSSODomains(
		methodConfigFields[IdPCommonInputFieldName.SSODomains],
		configsData,
		method
	);

	return updated;
};

const filterAndDeduplicateMappings = (
	mappings: IUserAttributeMapping[]
): IUserAttributeMapping[] => {
	const seen: Record<string, string[]> = {};

	return mappings.filter(({ idpAttr, spAttr }) => {
		if (!idpAttr && !spAttr) return false;

		const srcVal = idpAttr || '';
		const destVal = spAttr || '';

		if (seen[srcVal]?.includes(destVal)) return false;

		seen[srcVal] = seen[srcVal]?.concat(destVal) || [destVal];
		return true;
	});
};

const userAttributesMappingsToQueryMappings = (
	userAttributesMappings: IUserAttributeMapping[],
	groupsAttributeName: string | null,
	userAttributes: IResAttribute[]
): IConfigMapping => {
	const customUserAttributes = Object.fromEntries(
		userAttributes
			.filter((userAttribute) => userAttribute.custom)
			.map((userAttribute) => [userAttribute.value, true])
	);

	const configMappings: IConfigMapping = {};
	const customAttributes: Record<string, string> = {};

	filterAndDeduplicateMappings(userAttributesMappings)
		.filter(({ idpAttr, spAttr }) => idpAttr && spAttr)
		.forEach(({ idpAttr, spAttr }) => {
			if (!customUserAttributes[spAttr!]) {
				configMappings[spAttr!] = idpAttr!;
			} else {
				customAttributes[spAttr!] = idpAttr!;
			}
		});

	if (!isEmpty(customAttributes)) {
		configMappings.customAttributes = customAttributes;
	}

	if (groupsAttributeName) {
		configMappings[GROUP] = groupsAttributeName;
	}

	return configMappings;
};

const processGroupsMappings = (
	groupsAttributeMappings: IGroupsAttributeMapping[] | null | undefined
): { groups: string[]; role: { id: string } }[] => {
	if (!groupsAttributeMappings) {
		return [];
	}

	return groupsAttributeMappings
		.filter(({ group, roleId }) => group && roleId)
		.reduce(
			(acc, { group, roleId }) => {
				const existingMapping = acc.find(
					(mapping) => mapping.role.id === roleId
				);
				if (existingMapping) {
					existingMapping.groups.push(group!);
				} else {
					acc.push({ groups: [group!], role: { id: roleId! } });
				}
				return acc;
			},
			[] as { groups: string[]; role: { id: string } }[]
		);
};

export const updateQuerySamlWithFields = (
	samlConfigFields: IMethodConfigFields,
	configsData: IResConfigs,
	userAttributes: IResAttribute[],
	idpId?: string
): IResConfigs => {
	const updatedConfig = updateCommonFields(
		samlConfigFields,
		configsData,
		Method.Saml
	);

	const {
		[IdPSamlInputFieldName.IdPMetadataUrl]: idpMetadataURL,
		[IdPSamlInputFieldName.IdPIssuer]: entityID,
		[IdPSamlInputFieldName.IdPCertificate]: idpCert,
		[IdPSamlInputFieldName.IdPSsoUrl]: idpURL,
		[IdPCommonInputFieldName.GroupsAttributeMapping]: groupsAttributeMappings,
		[IdPCommonInputFieldName.GroupsAttribute]: groupsAttributeName,
		[IdPCommonInputFieldName.UserAttributes]: userAttributesMappings,
		[IdPCommonInputFieldName.DefaultSSORoles]: defaultSSORoles,
		[IdPCommonInputFieldName.RebacGroupsMappings]: rebacGroupsMappings
	} = samlConfigFields || {};

	const samlMappings = userAttributesMappings
		? userAttributesMappingsToQueryMappings(
				userAttributesMappings,
				groupsAttributeName?.idpAttr || null,
				userAttributes
			)
		: {};

	const samlGroupsMappings = processGroupsMappings(groupsAttributeMappings);

	const samlOverrides: Partial<ISamlConfig> = castTypeExcludeNullValues(
		filterObjectByValues(
			{
				idpMetadataURL,
				useMetaInfoToggle: !!idpMetadataURL,
				entityID,
				idpCert,
				idpURL,
				samlMappings,
				samlGroupsMappings,
				providerID: idpId,
				defaultSSORoles,
				rebacGroupsMappings
			},
			(v) => v !== undefined
		)
	);

	return {
		...updatedConfig!,
		saml: {
			...updatedConfig!.saml,
			...samlOverrides
		}
	};
};

/**
 * Serializes only the mapping fields (user attributes, groups, default roles,
 * FGA) into the method's configuration, leaving all connection fields (URLs,
 * certificates, secrets, provider id) untouched. Used by the SCIM save flow,
 * where mapping edits are persisted onto the SSO configuration (possibly
 * disabled) that drives SCIM provisioning.
 */
export const updateQueryMappingsWithFields = (
	methodConfigFields: IMethodConfigFields,
	configsData: IResConfigs,
	userAttributes: IResAttribute[],
	method: Method
): IResConfigs => {
	const {
		[IdPCommonInputFieldName.UserAttributes]: userAttributesMappings,
		[IdPCommonInputFieldName.GroupsAttributeMapping]: groupsAttributeMappings,
		[IdPCommonInputFieldName.GroupsAttribute]: groupsAttributeName,
		[IdPCommonInputFieldName.DefaultSSORoles]: defaultSSORoles,
		[IdPCommonInputFieldName.RebacGroupsMappings]: rebacGroupsMappings
	} = methodConfigFields || {};

	const mappings = userAttributesMappings
		? userAttributesMappingsToQueryMappings(
				userAttributesMappings,
				groupsAttributeName?.idpAttr || null,
				userAttributes
			)
		: {};

	const groupsMappings = processGroupsMappings(groupsAttributeMappings);

	if (method === Method.Saml) {
		const samlOverrides: Partial<ISamlConfig> = castTypeExcludeNullValues(
			filterObjectByValues(
				{
					samlMappings: mappings,
					samlGroupsMappings: groupsMappings,
					defaultSSORoles,
					rebacGroupsMappings
				},
				(v) => v !== undefined
			)
		);

		return {
			...configsData,
			saml: {
				...configsData.saml,
				...samlOverrides
			}
		};
	}

	const tenantId = configsData.tenant.id;
	const oidcOverrides: Partial<IOAuthConfig['providerSettings'][string]> =
		castTypeExcludeNullValues(
			filterObjectByValues(
				{
					userDataClaimsMapping: mappings,
					oidcGroupsMappings: groupsMappings,
					defaultSSORoles,
					rebacGroupsMappings
				},
				(v) => v !== undefined
			)
		);

	return {
		...configsData,
		oauth: {
			...configsData.oauth,
			providerSettings: {
				...configsData.oauth.providerSettings,
				[tenantId]: {
					...configsData.oauth.providerSettings[tenantId],
					...oidcOverrides
				}
			}
		}
	};
};

export const updateQueryOidcWithFields = (
	oidcConfigFields: IMethodConfigFields,
	configsData: IResConfigs,
	userAttributes: IResAttribute[],
	idpId?: string
): IResConfigs => {
	const updatedConfig = updateCommonFields(
		oidcConfigFields,
		configsData,
		Method.Oidc
	);

	const {
		[IdPOidcInputFieldName.ProviderName]: name,
		[IdPOidcInputFieldName.ClientId]: clientID,
		[IdPOidcInputFieldName.ClientSecret]: clientSecret,
		[IdPOidcInputFieldName.Scopes]: scope,
		[IdPOidcInputFieldName.GrantType]: grantType,
		[IdPOidcInputFieldName.Issuer]: issuer,
		[IdPOidcInputFieldName.AuthorizationEndpoint]: authURL,
		[IdPOidcInputFieldName.TokenEndpoint]: tokenURL,
		[IdPOidcInputFieldName.UserInfoEndpoint]: userDataURL,
		[IdPOidcInputFieldName.JWKsEndpoint]: JWKsURI,
		[IdPOidcInputFieldName.Prompt]: prompt,
		[IdPOidcInputFieldName.UsePkce]: usePkce,
		[IdPCommonInputFieldName.UserAttributes]: userAttributesMappings,
		[IdPCommonInputFieldName.GroupsAttributeMapping]: groupsAttributeMappings,
		[IdPCommonInputFieldName.GroupsAttribute]: groupsAttributeName,
		[IdPCommonInputFieldName.DefaultSSORoles]: defaultSSORoles,
		[IdPCommonInputFieldName.RebacGroupsMappings]: rebacGroupsMappings
	} = oidcConfigFields || {};

	const userDataClaimsMapping = userAttributesMappings
		? userAttributesMappingsToQueryMappings(
				userAttributesMappings,
				groupsAttributeName?.idpAttr || null,
				userAttributes
			)
		: {};

	const oidcGroupsMappings = processGroupsMappings(groupsAttributeMappings);

	const oidcOverrides: Partial<IOAuthConfig['providerSettings'][string]> =
		castTypeExcludeNullValues(
			filterObjectByValues(
				{
					enabled: true,
					name,
					clientID,
					clientSecret,
					scope,
					grantType,
					issuer,
					tokenURL,
					authURL,
					userDataURL,
					JWKsURI,
					prompt,
					// unset keeps the stored value: undefined is dropped, null would post as false
					usePkce: usePkce ?? undefined,
					userDataClaimsMapping,
					oidcGroupsMappings,
					defaultSSORoles,
					rebacGroupsMappings,
					...(idpId ? { providerID: idpId } : {})
				},
				(v) => v !== undefined
			)
		);

	const tenantId = updatedConfig.tenant.id;

	return {
		...updatedConfig,
		oauth: {
			...updatedConfig?.oauth,
			providerSettings: {
				...updatedConfig?.oauth.providerSettings,
				[tenantId]: {
					...updatedConfig?.oauth.providerSettings[tenantId],
					...oidcOverrides
				}
			}
		}
	};
};

export const updateQueryXaaWithFields = (
	xaaConfigFields: IMethodConfigFields,
	configsData: IResConfigs,
	userAttributes: IResAttribute[],
	idpId?: string
): IResConfigs => {
	const {
		[IdPCommonInputFieldName.UserAttributes]: userAttributesMappings,
		[IdPCommonInputFieldName.GroupsAttributeMapping]: groupsAttributeMappings,
		[IdPCommonInputFieldName.GroupsAttribute]: groupsAttributeName,
		[IdPCommonInputFieldName.DefaultSSORoles]: defaultSSORoles,
		[IdPCommonInputFieldName.RebacGroupsMappings]: rebacGroupsMappings,
		[IdPXaaInputFieldName.Issuer]: issuer,
		[IdPXaaInputFieldName.JwksUri]: jwksUri,
		[IdPXaaInputFieldName.SignAlgorithm]: signAlgorithm,
		[IdPXaaInputFieldName.UserInfoUri]: userInfoUri,
		[IdPXaaInputFieldName.Jit]: jit
	} = xaaConfigFields || {};

	const attributeMapping = userAttributesMappings
		? userAttributesMappingsToQueryMappings(
				userAttributesMappings,
				groupsAttributeName?.idpAttr || null,
				userAttributes
			)
		: {};

	// The subject / login-id claim lives in the mapping (like the console's
	// UserAttributeIDMapping), not a dedicated trust field: it is the idp claim mapped
	// to externalID. Mirror it onto the issuer's externalIdFieldName. Leave undefined
	// when unmapped so an existing issuer's value is preserved.
	const externalIdFieldName =
		(userAttributesMappings ?? []).find(
			(mapping) => mapping.spAttr === EXTERNAL_ID
		)?.idpAttr || undefined;

	// JIT is the content-placeable xaa-jit field: 'disabled' -> jitDisabled=true. Left
	// unset when the field is absent so an existing issuer's value (which the shared JIT
	// section may own) is preserved. When present it round-trips the loaded value, so it
	// never clobbers what the JIT section set.
	const jitDisabled =
		jit === undefined || jit === null ? undefined : jit === XaaJit.Disabled;

	// Trust only - the issuer entry carries transport + attribute mapping. Config-level
	// group->role / default roles / FGA live on the top-level xaa object below.
	const issuerOverrides: Partial<IXaaIssuerSettings> =
		castTypeExcludeNullValues(
			filterObjectByValues(
				{
					jwksUri,
					signAlgorithm,
					userInfoUri,
					externalIdFieldName,
					jitDisabled,
					attributeMapping
				},
				(v) => v !== undefined
			)
		);

	// The suite edits the single active XAA issuer that queryXaaToXaaFields loaded into
	// the Issuer field. The trusted issuers live on the xaa row (jwtBearerSettings.issuers
	// - the record the backend persists); the tenant carries no XAA trust.
	const existingIssuers = configsData.xaa?.jwtBearerSettings?.issuers ?? {};

	// The edited key is the loaded key (the first entry queryXaaToXaaFields displayed),
	// not a fresh guess at save time. Fall back to the entered Issuer field on a fresh
	// setup where no issuer exists yet. Without either there is nowhere valid to attach
	// the config, so no-op.
	const [editedKey] = Object.keys(existingIssuers);
	const issuerKey = issuer || editedKey;

	if (!issuerKey) {
		return configsData;
	}

	// Preserve every OTHER issuer untouched (console may manage more than one); only the
	// edited issuer's entry is rewritten (or renamed to a brand-new key). The rename-onto-a-
	// DIFFERENT-existing-issuer collision (which would clobber that issuer's settings and drop
	// the edited one, silently collapsing two issuers into one) is rejected up front by
	// xaaValidationSchema as a field error on the Issuer input, so it is unreachable from the UI;
	// guard defensively by leaving the config untouched rather than merging.
	const preservedIssuers = Object.fromEntries(
		Object.entries(existingIssuers).filter(([key]) => key !== editedKey)
	);
	if (issuer && issuer !== editedKey && issuer in preservedIssuers) {
		return configsData;
	}
	const baseIssuer = editedKey ? existingIssuers[editedKey] : undefined;

	const issuers = {
		...preservedIssuers,
		[issuerKey]: {
			...baseIssuer,
			...issuerOverrides
		}
	};

	// Config-level group->role and FGA mirror SAML/OIDC (processGroupsMappings for
	// samlGroupsMappings, the FGA field passed through as-is). This top-level xaa object
	// is the ONLY place the backend persists the mapping; providerID mirrors how the
	// SAML/OIDC save sets providerID: idpId. Existing xaa fields are preserved.
	const samlGroupsMappings = processGroupsMappings(groupsAttributeMappings);

	const xaa: IXaaConfig = {
		...configsData.xaa,
		...castTypeExcludeNullValues(
			filterObjectByValues(
				{
					enabled: true,
					jwtBearerSettings: {
						...configsData.xaa?.jwtBearerSettings,
						issuers
					},
					samlGroupsMappings,
					defaultSSORoles,
					rebacGroupsMappings,
					...(idpId ? { providerID: idpId } : {})
				},
				(v) => v !== undefined
			)
		)
	};

	return {
		...configsData,
		xaa
	};
};
